"use client"

import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Database, RefreshCw, Trash2, CheckCircle, XCircle, AlertTriangle, Loader2, Activity } from "lucide-react"
import { checkDatabaseConsistency, cleanupDatabase, getDatabaseStats } from "../../../lib/api/api"
import { formatDateTime } from "../../../shared/utils/format"

interface Statistics {
  documents?: {
    postgres_total: number
    pinecone_total: number
    missing_embeddings: number
    orphaned_vectors: number
    consistent: number
  }
  articles?: {
    postgres_total: number
    pinecone_total: number
    missing_embeddings: number
    orphaned_vectors: number
    articles_without_parent: number
    consistent: number
  }
}

interface Recommendation {
  issue: string
  count: number
  action: string
  priority: "HIGH" | "MEDIUM" | "LOW"
}

interface ConsistencyReport {
  timestamp: string
  summary: {
    total_issues: number
    checks_performed: string[]
    status: string
  }
  statistics: Statistics
  inconsistencies: {
    [key: string]: any[]
  }
  recommendations: Recommendation[]
}

interface QuickStats {
  timestamp: string
  postgres: { [key: string]: number }
  pinecone: { [key: string]: { total_vectors: number; dimension: number } }
  consistency?: {
    documents: {
      total_postgres: number
      total_pinecone: number
      matched: number
      missing_embeddings: number
      orphaned_vectors: number
    }
    articles: {
      total_postgres: number
      total_pinecone: number
      matched: number
      missing_embeddings: number
      orphaned_vectors: number
    }
  }
}

export default function DatabaseMaintenance() {
  const { t } = useTranslation()
  const [isChecking, setIsChecking] = useState(false)
  const [isCleaning, setIsCleaning] = useState(false)
  const [isLoadingStats, setIsLoadingStats] = useState(false)
  const [report, setReport] = useState<ConsistencyReport | null>(null)
  const [quickStats, setQuickStats] = useState<QuickStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false)

  const handleCheckConsistency = async () => {
    try {
      setIsChecking(true)
      setError(null)
      setSuccessMessage(null)

      const response = await checkDatabaseConsistency()
      setReport(response.data)

      setSuccessMessage(t('platform.maintenance.checkSuccess'))
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (error: any) {
      console.error("Failed to check consistency:", error)
      setError(error.response?.data?.detail || t('platform.maintenance.errors.checkFailed'))
    } finally {
      setIsChecking(false)
    }
  }

  const handleCleanup = async () => {
    try {
      setIsCleaning(true)
      setError(null)
      setSuccessMessage(null)

      const response = await cleanupDatabase({ confirm: true })

      setSuccessMessage(t('platform.maintenance.cleanupSuccess', {
        deleted: response.data.statistics.total_deleted,
        failed: response.data.statistics.failed_deletes
      }))

      // Refresh consistency check after cleanup
      await handleCheckConsistency()

      setShowCleanupConfirm(false)
    } catch (error: any) {
      console.error("Failed to cleanup:", error)
      setError(error.response?.data?.detail || t('platform.maintenance.errors.cleanupFailed'))
    } finally {
      setIsCleaning(false)
    }
  }

  const handleGetQuickStats = async () => {
    try {
      setIsLoadingStats(true)
      setError(null)

      const response = await getDatabaseStats()
      setQuickStats(response.data)
    } catch (error: any) {
      console.error("Failed to get stats:", error)
      setError(error.response?.data?.detail || t('platform.maintenance.errors.statsFailed'))
    } finally {
      setIsLoadingStats(false)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "HIGH":
        return "bg-red-100 text-red-700 border-red-300"
      case "MEDIUM":
        return "bg-yellow-100 text-yellow-700 border-yellow-300"
      case "LOW":
        return "bg-green-100 text-green-700 border-green-300"
      default:
        return "bg-gray-100 text-gray-700 border-gray-300"
    }
  }

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case "HIGH":
        return <XCircle className="w-4 h-4" />
      case "MEDIUM":
        return <AlertTriangle className="w-4 h-4" />
      case "LOW":
        return <CheckCircle className="w-4 h-4" />
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('platform.maintenance.title')}</h2>
          <p className="text-gray-600 mt-1">
            {t('platform.maintenance.subtitle')}
          </p>
        </div>
        <Database className="w-8 h-8 text-primary-500" />
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <XCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
          <span className="text-green-700">{successMessage}</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={handleGetQuickStats}
          disabled={isLoadingStats}
          className="flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoadingStats ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Activity className="w-5 h-5 mr-2" />
          )}
          {t('platform.maintenance.quickStats')}
        </button>

        <button
          onClick={handleCheckConsistency}
          disabled={isChecking}
          className="flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isChecking ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-5 h-5 mr-2" />
          )}
          {t('platform.maintenance.checkConsistency')}
        </button>

        <button
          onClick={() => setShowCleanupConfirm(true)}
          disabled={!report || report.summary.total_issues === 0 || isCleaning}
          className="flex items-center justify-center px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCleaning ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Trash2 className="w-5 h-5 mr-2" />
          )}
          {t('platform.maintenance.runCleanup')}
        </button>
      </div>

      {/* Quick Stats Display */}
      {quickStats && (
        <div className="space-y-4">
          {/* Consistency Summary Cards */}
          {quickStats.consistency && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Documents Consistency */}
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Database className="w-5 h-5 mr-2 text-primary-500" />
                  {t('platform.maintenance.documentsConsistencyTitle')}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('platform.maintenance.sqliteTotal')}</span>
                    <span className="font-bold text-primary-600">{quickStats.consistency.documents.total_postgres}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('platform.maintenance.qdrantTotal')}</span>
                    <span className="font-bold text-purple-600">{quickStats.consistency.documents.total_pinecone}</span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-green-700">{t('platform.maintenance.matched')}</span>
                      <span className="font-bold text-green-600">{quickStats.consistency.documents.matched}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm font-medium text-yellow-700">{t('platform.maintenance.missingEmbeddings')}</span>
                      <span className="font-bold text-yellow-600">{quickStats.consistency.documents.missing_embeddings}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm font-medium text-red-700">{t('platform.maintenance.orphanedVectors')}</span>
                      <span className="font-bold text-red-600">{quickStats.consistency.documents.orphaned_vectors}</span>
                    </div>
                  </div>
                  {quickStats.consistency.documents.total_postgres > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">{t('platform.maintenance.matchRate')}</span>
                        <span className={`font-bold text-lg ${
                          quickStats.consistency.documents.matched === quickStats.consistency.documents.total_postgres
                            ? 'text-green-600'
                            : 'text-orange-600'
                        }`}>
                          {Math.round((quickStats.consistency.documents.matched / quickStats.consistency.documents.total_postgres) * 100)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Articles Consistency */}
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Database className="w-5 h-5 mr-2 text-primary-500" />
                  {t('platform.maintenance.articlesConsistencyTitle')}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('platform.maintenance.sqliteTotal')}</span>
                    <span className="font-bold text-primary-600">{quickStats.consistency.articles.total_postgres}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('platform.maintenance.qdrantTotal')}</span>
                    <span className="font-bold text-purple-600">{quickStats.consistency.articles.total_pinecone}</span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-green-700">{t('platform.maintenance.matched')}</span>
                      <span className="font-bold text-green-600">{quickStats.consistency.articles.matched}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm font-medium text-yellow-700">{t('platform.maintenance.missingEmbeddings')}</span>
                      <span className="font-bold text-yellow-600">{quickStats.consistency.articles.missing_embeddings}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm font-medium text-red-700">{t('platform.maintenance.orphanedVectors')}</span>
                      <span className="font-bold text-red-600">{quickStats.consistency.articles.orphaned_vectors}</span>
                    </div>
                  </div>
                  {quickStats.consistency.articles.total_postgres > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">{t('platform.maintenance.matchRate')}</span>
                        <span className={`font-bold text-lg ${
                          quickStats.consistency.articles.matched === quickStats.consistency.articles.total_postgres
                            ? 'text-green-600'
                            : 'text-orange-600'
                        }`}>
                          {Math.round((quickStats.consistency.articles.matched / quickStats.consistency.articles.total_postgres) * 100)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Raw Counts (Collapsed) */}
          <details className="bg-gray-50 rounded-lg p-6">
            <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
              {t('platform.maintenance.rawCountsSummary')}
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              {/* SQLite Stats */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3 flex items-center">
                  <Database className="w-4 h-4 mr-2" />
                  {t('platform.maintenance.sqliteRowsTitle')}
                </h4>
                <div className="space-y-2">
                  {Object.entries(quickStats.postgres).map(([table, count]) => (
                    <div key={table} className="flex justify-between text-sm">
                      <span className="text-gray-600">{table}:</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Qdrant Stats */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3 flex items-center">
                  <Database className="w-4 h-4 mr-2" />
                  {t('platform.maintenance.qdrantVectorsTitle')}
                </h4>
                <div className="space-y-2">
                  {Object.entries(quickStats.pinecone).map(([index, stats]) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-gray-600">{index}:</span>
                      <span className="font-medium">{stats.total_vectors} {t('platform.maintenance.vectorsUnit')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </details>

          <p className="text-xs text-gray-500 text-center">
            {t('platform.maintenance.updatedAt')} {formatDateTime(quickStats.timestamp)}
          </p>
        </div>
      )}

      {/* Consistency Report */}
      {report && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Issues */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('platform.maintenance.totalIssues')}</p>
                  <p className="text-2xl font-bold text-gray-800">{report.summary.total_issues}</p>
                </div>
                <AlertTriangle className={`w-8 h-8 ${report.summary.total_issues > 0 ? 'text-yellow-500' : 'text-green-500'}`} />
              </div>
            </div>

            {/* Documents Stats */}
            {report.statistics.documents && (
              <>
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('platform.maintenance.documentsConsistent')}</p>
                      <p className="text-2xl font-bold text-green-600">
                        {report.statistics.documents.consistent}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('platform.maintenance.missingEmbeddingsCard')}</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {report.statistics.documents.missing_embeddings}
                      </p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-yellow-500" />
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('platform.maintenance.orphanedVectorsCard')}</p>
                      <p className="text-2xl font-bold text-red-600">
                        {report.statistics.documents.orphaned_vectors}
                      </p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-500" />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Articles Stats */}
          {report.statistics.articles && (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">{t('platform.maintenance.articlesStatsTitle')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-sm text-gray-600">{t('platform.maintenance.sqliteLabel')}</p>
                  <p className="text-xl font-bold">{report.statistics.articles.postgres_total}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('platform.maintenance.qdrantLabel')}</p>
                  <p className="text-xl font-bold">{report.statistics.articles.pinecone_total}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('platform.maintenance.consistentLabel')}</p>
                  <p className="text-xl font-bold text-green-600">{report.statistics.articles.consistent}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('platform.maintenance.missingEmbeddingLabel')}</p>
                  <p className="text-xl font-bold text-yellow-600">{report.statistics.articles.missing_embeddings}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('platform.maintenance.missingParentLabel')}</p>
                  <p className="text-xl font-bold text-red-600">{report.statistics.articles.articles_without_parent}</p>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {report.recommendations && report.recommendations.length > 0 && (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">{t('platform.maintenance.recommendationsTitle')}</h3>
              <div className="space-y-3">
                {report.recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 ${getPriorityColor(rec.priority)}`}
                  >
                    <div className="flex items-start">
                      <div className="mr-3 mt-1">{getPriorityIcon(rec.priority)}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold">{rec.issue}</h4>
                          <span className="text-sm font-medium">{t('platform.maintenance.itemsCount', { count: rec.count })}</span>
                        </div>
                        <p className="text-sm">{rec.action}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamp */}
          <p className="text-sm text-gray-500 text-center">
            {t('platform.lastCheck')} {formatDateTime(report.timestamp)}
          </p>
        </div>
      )}

      {/* Cleanup Confirmation Modal */}
      {showCleanupConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500 mr-2" />
              <h3 className="text-lg font-semibold">{t('platform.maintenance.confirmCleanupTitle')}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {t('platform.maintenance.confirmCleanupBody')}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCleanupConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCleanup}
                disabled={isCleaning}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {isCleaning ? t('platform.maintenance.cleaningInProgress') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!report && !quickStats && !isChecking && !isLoadingStats && (
        <div className="text-center py-12">
          <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">{t('platform.maintenance.noReportTitle')}</h3>
          <p className="text-gray-500">
            {t('platform.maintenance.noReportHint')}
          </p>
        </div>
      )}
    </div>
  )
}
